import { useState } from "react";
import Button from "../ui/button/Button";
import { NMDocument, getCombinedPDFUrl } from "../../lib/api/nm";

interface NMDocumentListProps {
  api: string;
  documents: NMDocument[];
  loading?: boolean;
}

/**
 * Parse an embedded date from an NM OCD filename.
 * Handles the form: 30015288410000_07_31_2018_02_37_53.pdf
 * Returns a formatted string like "Jul 31, 2018", or null if no date found.
 */
function parseFilingDate(filename: string): string | null {
  const match = filename.match(/_(\d{2})_(\d{2})_(\d{4})_/);
  if (!match) return null;
  const [, month, day, year] = match;
  try {
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

/**
 * Compute the human-readable primary display name for a document.
 */
function displayName(doc: NMDocument): string {
  if (doc.doc_type_display) return doc.doc_type_display;
  const datePart = parseFilingDate(doc.filename);
  return datePart ? `NM OCD Document — ${datePart}` : "NM OCD Document";
}

/**
 * Format file size for display
 */
function formatFileSize(size: string | null): string {
  if (!size) return "";
  return size;
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function NMDocumentList({ api, documents, loading = false }: NMDocumentListProps) {
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    setDownloadError(null);

    try {
      const response = await getCombinedPDFUrl(api);
      // Open the combined PDF URL in a new tab
      window.open(response.url, "_blank");
    } catch (err) {
      console.error("Error getting combined PDF:", err);
      setDownloadError(err instanceof Error ? err.message : "Failed to get combined PDF");
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSingle = (doc: NMDocument) => {
    // Open individual document in new tab
    window.open(doc.url, "_blank");
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Documents
          </h3>
        </div>
        <div className="px-6 py-8">
          <div className="flex items-center justify-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-r-transparent"></div>
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
              Loading documents...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-white/[0.05]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Documents
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {documents.length} document{documents.length !== 1 ? "s" : ""} available
            </p>
          </div>
          {documents.length > 0 && (
            <Button
              onClick={handleDownloadAll}
              disabled={downloadingAll}
              variant="outline"
              size="sm"
            >
              {downloadingAll ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-r-transparent"></span>
                  Loading...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download All (Combined PDF)
                </>
              )}
            </Button>
          )}
        </div>
        {downloadError && (
          <div className="mt-2 rounded-lg bg-error-50 p-2 dark:bg-error-500/15">
            <p className="text-xs text-error-700 dark:text-error-400">
              {downloadError}
            </p>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-6 py-4">
        {documents.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No documents available for this well
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/[0.05] dark:bg-white/[0.02] hover:bg-gray-100 dark:hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  {/* PDF Icon */}
                  <div className="flex-shrink-0">
                    <svg
                      className="h-8 w-8 text-error-500"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 15.5a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 1 0v3a.5.5 0 0 1-.5.5zm3 0a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 1 0v3a.5.5 0 0 1-.5.5zm3 0a.5.5 0 0 1-.5-.5v-3a.5.5 0 0 1 1 0v3a.5.5 0 0 1-.5.5z"/>
                    </svg>
                  </div>

                  {/* File Info */}
                  <div className="min-w-0 flex-1" title={doc.filename}>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {displayName(doc)}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">
                      {doc.filename}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {doc.date && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(doc.date)}
                        </span>
                      )}
                      {doc.file_size && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {formatFileSize(doc.file_size)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownloadSingle(doc)}
                  className="flex-shrink-0 ml-4 p-2 text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400 transition-colors"
                  title="Download"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
